import React from "react";
import { useForm } from "@inertiajs/react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Eye, EyeOff, Info } from "lucide-react";

interface Props {
  errors?: {
    current_password?: string[];
    password?: string[];
    password_confirmation?: string[];
  };
}

export default function ChangePassword({ errors }: Props) {
  const { data, setData, patch, processing } = useForm({
    current_password: "",
    password: "",
    password_confirmation: "",
  });

  const [showCurrent, setShowCurrent] = React.useState(false);
  const [showNew, setShowNew] = React.useState(false);
  const [showConfirmation, setShowConfirmation] = React.useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    patch("/compte/mot-de-passe");
  };

  return (
    <div className="flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
          Modifier le mot de passe
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {errors && Object.keys(errors).length > 0 && (
              <Alert variant="destructive">
                <AlertDescription>
                  {Object.values(errors).flat().map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </AlertDescription>
              </Alert>
            )}

            <div>
              <label htmlFor="current_password" className="block text-sm font-medium text-gray-700">
                Mot de passe actuel
              </label>
              <div className="mt-1 relative">
                <Input
                  id="current_password"
                  name="current_password"
                  type={showCurrent ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={data.current_password}
                  onChange={e => setData("current_password", e.target.value)}
                  className={`${errors?.current_password ? "border-red-500" : ""} pr-10`}
                />
                <button
                  type="button"
                  aria-label={showCurrent ? "Masquer" : "Afficher"}
                  onClick={() => setShowCurrent(p => !p)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                >
                  {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Nouveau mot de passe
              </label>
              <div className="mt-1 relative">
                <Input
                  id="password"
                  name="password"
                  type={showNew ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={data.password}
                  onChange={e => setData("password", e.target.value)}
                  className={`${errors?.password ? "border-red-500" : ""} pr-10`}
                />
                <button
                  type="button"
                  aria-label={showNew ? "Masquer" : "Afficher"}
                  onClick={() => setShowNew(p => !p)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                >
                  {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                <Info size={14} />
                Le mot de passe doit contenir au moins 8 caractères.
              </p>
            </div>

            <div>
              <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700">
                Confirmer le nouveau mot de passe
              </label>
              <div className="mt-1 relative">
                <Input
                  id="password_confirmation"
                  name="password_confirmation"
                  type={showConfirmation ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={data.password_confirmation}
                  onChange={e => setData("password_confirmation", e.target.value)}
                  className={`${errors?.password_confirmation ? "border-red-500" : ""} pr-10`}
                />
                <button
                  type="button"
                  aria-label={showConfirmation ? "Masquer" : "Afficher"}
                  onClick={() => setShowConfirmation(p => !p)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmation ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <Button
                type="submit"
                className="w-full"
                disabled={processing}
              >
                {processing ? "Enregistrement..." : "Modifier le mot de passe"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
